import React, { useState, useEffect, useRef } from 'react';
import { Command, Search, X } from 'lucide-react';
import { AnimatedModal } from './AnimatedComponents';
import { useCommandPalette, useKeyboardNavigation } from '../hooks/useKeyboardNavigation';
import { motion } from 'framer-motion';

interface CommandItem {
  id: string;
  title: string;
  description?: string;
  icon?: React.ReactNode;
  shortcut?: string;
  action: () => void;
  category?: string;
}

const defaultCommands: CommandItem[] = [
  // Navigation
  {
    id: 'nav-home',
    title: 'Ana Sayfa',
    description: 'Ana sayfaya git',
    category: 'Navigasyon',
    action: () => window.location.href = '/'
  },
  {
    id: 'nav-whiteboard',
    title: 'Whiteboard',
    description: 'Whiteboard\'a git',
    category: 'Navigasyon',
    action: () => window.location.href = '/whiteboard'
  },
  {
    id: 'nav-library',
    title: 'Edesis Soru Havuzu Dijital Akıllı Tahta',
    description: 'Testleri görüntüle',
    category: 'Navigasyon',
    action: () => window.location.href = '/library'
  },
  
  // Tools (Whiteboard specific)
  {
    id: 'tool-pencil',
    title: 'Kalem',
    description: 'Kalem aracını seç',
    shortcut: 'P',
    category: 'Araçlar',
    action: () => {
      const event = new CustomEvent('select-tool', { detail: 'pencil' });
      window.dispatchEvent(event);
    }
  },
  {
    id: 'tool-eraser',
    title: 'Silgi',
    description: 'Silgi aracını seç',
    shortcut: 'E',
    category: 'Araçlar',
    action: () => {
      const event = new CustomEvent('select-tool', { detail: 'eraser' });
      window.dispatchEvent(event);
    }
  },
  
  // Actions
  {
    id: 'action-undo',
    title: 'Geri Al',
    description: 'Son işlemi geri al',
    shortcut: 'Ctrl+Z',
    category: 'İşlemler',
    action: () => {
      const event = new CustomEvent('undo');
      window.dispatchEvent(event);
    }
  },
  {
    id: 'action-redo',
    title: 'Yinele',
    description: 'Geri alınan işlemi yinele',
    shortcut: 'Ctrl+Shift+Z',
    category: 'İşlemler',
    action: () => {
      const event = new CustomEvent('redo');
      window.dispatchEvent(event);
    }
  },
  {
    id: 'action-clear',
    title: 'Tahtayı Temizle',
    description: 'Tüm çizimleri sil',
    category: 'İşlemler',
    action: () => {
      const event = new CustomEvent('clear-board');
      window.dispatchEvent(event);
    }
  },
  
  // Settings
  {
    id: 'settings-dark-mode',
    title: 'Karanlık Mod',
    description: 'Karanlık modu aç/kapat',
    category: 'Ayarlar',
    action: () => {
      const event = new CustomEvent('toggle-dark-mode');
      window.dispatchEvent(event);
    }
  },
  {
    id: 'settings-fullscreen',
    title: 'Tam Ekran',
    description: 'Tam ekran modunu aç/kapat',
    shortcut: 'F11',
    category: 'Ayarlar',
    action: () => {
      if (document.fullscreenElement) {
        document.exitFullscreen();
      } else {
        document.documentElement.requestFullscreen();
      }
    }
  }
];

export const CommandPalette: React.FC = () => {
  const { isOpen, setIsOpen } = useCommandPalette();
  const [search, setSearch] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [commands] = useState<CommandItem[]>(defaultCommands);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  
  // Note: trapFocus would be used for keyboard navigation implementation
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { trapFocus: _trapFocus } = useKeyboardNavigation();

  // Filter commands based on search
  const filteredCommands = commands.filter(cmd => {
    const searchLower = search.toLowerCase();
    return (
      cmd.title.toLowerCase().includes(searchLower) ||
      cmd.description?.toLowerCase().includes(searchLower) ||
      cmd.category?.toLowerCase().includes(searchLower)
    );
  });

  // Group commands by category
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    const category = cmd.category || 'Diğer';
    if (!acc[category]) acc[category] = [];
    acc[category].push(cmd);
    return acc;
  }, {} as Record<string, CommandItem[]>);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < filteredCommands.length - 1 ? prev + 1 : 0
          );
          break;
          
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev > 0 ? prev - 1 : filteredCommands.length - 1
          );
          break;
          
        case 'Enter':
          e.preventDefault();
          if (filteredCommands[selectedIndex]) {
            filteredCommands[selectedIndex].action();
            setIsOpen(false);
          }
          break;
          
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands, setIsOpen]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current && selectedIndex >= 0) {
      const items = listRef.current.querySelectorAll('[data-command-item]');
      items[selectedIndex]?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  return (
    <AnimatedModal
      isOpen={isOpen}
      onClose={() => setIsOpen(false)}
      className="w-full max-w-2xl"
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl overflow-hidden">
        {/* Search Input */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              ref={inputRef}
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder="Komut ara..."
              className="w-full pl-10 pr-10 py-3 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-white"
            />
            <button
              onClick={() => setIsOpen(false)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Commands List */}
        <div ref={listRef} className="max-h-96 overflow-y-auto p-2">
          {Object.entries(groupedCommands).map(([category, items]) => (
            <div key={category} className="mb-4">
              <h3 className="px-3 py-1 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
                {category}
              </h3>
              {items.map((cmd) => {
                const globalIndex = filteredCommands.indexOf(cmd);
                const isSelected = globalIndex === selectedIndex;
                
                return (
                  <motion.button
                    key={cmd.id}
                    data-command-item
                    onClick={() => {
                      cmd.action();
                      setIsOpen(false);
                    }}
                    onMouseEnter={() => setSelectedIndex(globalIndex)}
                    whileHover={{ scale: 1.01 }}
                    whileTap={{ scale: 0.99 }}
                    className={`
                      w-full flex items-center justify-between px-3 py-2 rounded-lg text-left transition-colors
                      ${isSelected 
                        ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' 
                        : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'
                      }
                    `}
                  >
                    <div className="flex items-center space-x-3">
                      {cmd.icon && (
                        <div className="w-5 h-5 flex-shrink-0">
                          {cmd.icon}
                        </div>
                      )}
                      <div>
                        <div className="font-medium">{cmd.title}</div>
                        {cmd.description && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {cmd.description}
                          </div>
                        )}
                      </div>
                    </div>
                    {cmd.shortcut && (
                      <kbd className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 rounded">
                        {cmd.shortcut}
                      </kbd>
                    )}
                  </motion.button>
                );
              })}
            </div>
          ))}

          {filteredCommands.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              Komut bulunamadı
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <div className="flex items-center space-x-4">
              <span className="flex items-center">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">↑</kbd>
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded ml-1">↓</kbd>
                <span className="ml-2">Gezin</span>
              </span>
              <span className="flex items-center">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">Enter</kbd>
                <span className="ml-2">Seç</span>
              </span>
              <span className="flex items-center">
                <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">Esc</kbd>
                <span className="ml-2">Kapat</span>
              </span>
            </div>
            <span className="flex items-center">
              <Command className="w-3 h-3 mr-1" />
              <kbd className="px-1.5 py-0.5 bg-gray-200 dark:bg-gray-700 rounded">Ctrl+K</kbd>
            </span>
          </div>
        </div>
      </div>
    </AnimatedModal>
  );
};